import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthShell, { Field, inputClass } from "@/components/AuthShell";
import { PrimaryButton } from "@/components/kit";
import { apiFetch, storeToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ForgotPassword() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = request code, 2 = reset
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiFetch("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email: email.trim() }) }, false);
      toast.success("If that email exists, a 6-digit code is on its way.");
      setStep(2);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters.");
    setLoading(true);
    try {
      const res = await apiFetch(
        "/auth/reset-password",
        { method: "POST", body: JSON.stringify({ email: email.trim(), code: code.trim(), new_password: newPassword }) },
        false
      );
      storeToken(res.token);
      await refresh();
      toast.success("Password reset! You're now logged in.");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(err.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle={step === 1 ? "We'll email you a 6-digit code." : "Enter the code we sent and choose a new password."}
      footer={<Link to="/login" className="font-bold text-[#E86A45]">Back to log in</Link>}
    >
      {step === 1 ? (
        <form onSubmit={requestCode}>
          <Field label="Email">
            <input
              data-testid="forgot-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputClass}
            />
          </Field>
          <PrimaryButton data-testid="forgot-request-btn" type="submit" loading={loading} className="w-full">
            Send reset code
          </PrimaryButton>
        </form>
      ) : (
        <form onSubmit={reset}>
          <Field label="6-digit code">
            <input
              data-testid="forgot-code-input"
              inputMode="numeric"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className={inputClass + " tracking-[0.4em] text-center text-lg font-bold"}
            />
          </Field>
          <Field label="New password">
            <input
              data-testid="forgot-newpassword-input"
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              className={inputClass}
            />
          </Field>
          <PrimaryButton data-testid="forgot-reset-btn" type="submit" loading={loading} className="w-full">
            Reset password
          </PrimaryButton>
          <button
            type="button"
            onClick={() => setStep(1)}
            className="w-full mt-3 text-sm font-bold text-[#6F8F72]"
          >
            Didn&apos;t get a code? Try again
          </button>
        </form>
      )}
    </AuthShell>
  );
}
